import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import moment from "moment";
import "moment/locale/es";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import NotaDatoOriginal from "../../components/Common/NotaDatoOriginal";
import PageSpinner from "../../components/Common/PageSpinner";
import { connect } from "react-redux";
import { doc, getDocs, onSnapshot } from "firebase/firestore";
import { getVersionedCollection } from "../../utils/firestorePaths";

import TabResumen from "./TabResumen";
import TabIngresos from "./TabIngresos";
import TabTickets from "./TabTickets";
import TabMensual from "./TabMensual";
import TabAsistente from "./TabAsistente";

const TABS = [
  { id: "resumen", label: "Resumen", icon: "dashboard" },
  { id: "ingresos", label: "Ingresos", icon: "attach_money" },
  { id: "tickets", label: "Tickets", icon: "confirmation_number" },
  { id: "mensual", label: "Análisis mensual", icon: "calendar_month" },
  { id: "asistente", label: "Asistente IA", icon: "smart_toy" },
];

const Stats = ({ company_id, syncVersion, versionDB, pie }) => {
  const [activeTab, setActiveTab] = useState("resumen");
  const [liveData, setLiveData] = useState(null);
  const [dailyDocs, setDailyDocs] = useState([]);
  const [monthlyDocs, setMonthlyDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideMobileTip, setHideMobileTip] = useState(() => localStorage.getItem("stats_hide_mobile_tip") === "1");

  document.title = "Estadísticas | TicketControl Monitor";

  // Real-time listener for live doc
  useEffect(() => {
    if (!company_id) return;
    const statsRef = getVersionedCollection(company_id, "stats", syncVersion);
    const liveRef = doc(statsRef, "live");

    const unsub = onSnapshot(
      liveRef,
      (snap) => {
        if (snap.exists()) setLiveData(snap.data());
      },
      (err) => console.error("Error listening to stats/live:", err)
    );

    return () => unsub();
  }, [company_id, syncVersion]);

  // Real-time listener for today's daily doc
  useEffect(() => {
    if (!company_id) return;
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
    const statsRef = getVersionedCollection(company_id, "stats", syncVersion);
    const todayRef = doc(statsRef, `daily_${today}`);

    const unsub = onSnapshot(
      todayRef,
      (snap) => {
        if (!snap.exists()) return;
        const updated = { id: snap.id, ...snap.data() };
        setDailyDocs((prev) => {
          const idx = prev.findIndex((d) => d.id === updated.id);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = updated;
            return copy;
          }
          // New doc: insert sorted desc
          return [updated, ...prev].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 90);
        });
      },
      (err) => console.error("Error listening to today's daily doc:", err)
    );

    return () => unsub();
  }, [company_id, syncVersion]);

  // Fetch all daily + monthly docs
  const fetchHistorical = useCallback(async () => {
    if (!company_id) return;
    setLoading(true);
    setError(null);

    try {
      const statsRef = getVersionedCollection(company_id, "stats", syncVersion);
      const snapshot = await getDocs(statsRef);

      const daily = [];
      const monthly = [];

      snapshot.forEach((docSnap) => {
        const id = docSnap.id;
        const data = docSnap.data();
        if (id.startsWith("daily_")) daily.push({ id, ...data });
        else if (id.startsWith("monthly_")) monthly.push({ id, ...data });
      });

      // Sort daily desc (newest first), keep last 90
      daily.sort((a, b) => b.date.localeCompare(a.date));
      setDailyDocs(daily.slice(0, 90));

      monthly.sort((a, b) => b.month.localeCompare(a.month));
      setMonthlyDocs(monthly);
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError("No se pudieron cargar las estadísticas. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  }, [company_id, syncVersion]);

  useEffect(() => {
    fetchHistorical();
  }, [fetchHistorical]);

  // Version check
  const needsUpdate = (() => {
    if (!versionDB || versionDB === "0.0.0") return true;
    try {
      const parts = versionDB.split(".").map(Number);
      const req = [1, 2, 220];
      for (let i = 0; i < 3; i++) {
        if ((parts[i] || 0) < req[i]) return true;
        if ((parts[i] || 0) > req[i]) return false;
      }
      return false;
    } catch {
      return true;
    }
  })();

  // ── Render guards ──────────────────────────────────────────

  if (needsUpdate) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Estadísticas" breadcrumbItem="Estadísticas" />
          <div className="card shadow-sm">
            <div className="card-body">
              <h5>Esta funcionalidad requiere una actualización de TicketControl</h5>
              <p className="text-muted mb-0">
                Requiere <b>TicketControl versión 1.2.220 o superior</b>.
                Actualiza TicketControl en tu estacionamiento para ver las estadísticas.
              </p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Estadísticas" breadcrumbItem="Estadísticas" />
          <PageSpinner />
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Estadísticas" breadcrumbItem="Estadísticas" />
          <div className="alert alert-danger">{error}</div>
        </Container>
      </div>
    );
  }

  const noData = !liveData && !dailyDocs.length && !monthlyDocs.length;

  if (noData) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Estadísticas" breadcrumbItem="Estadísticas" />
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <span className="material-symbols-rounded text-muted mb-3 d-block" style={{ fontSize: 48 }}>
                bar_chart
              </span>
              <h5 className="text-muted">Todavía no hay datos para mostrarte</h5>
              <p className="text-muted mb-0">
                Los datos se generan solos a medida que el estacionamiento
                opera. Vuelve a consultar más tarde.
              </p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Estadísticas" breadcrumbItem="Estadísticas" />

        {/* Mobile suggestion banner - only visible on small screens */}
        {!hideMobileTip && (
        <Alert color="warning" className="d-flex align-items-center mb-3 py-2 d-md-none" style={{ position: "relative" }}>
          <span className="material-symbols-rounded me-2" style={{ fontSize: 20 }}>desktop_windows</span>
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>Tip:</strong> Para una mejor experiencia con gráficos y tablas,
            te recomendamos acceder a las estadísticas desde una computadora o tablet.
          </div>
          <button type="button" className="btn-close ms-2" style={{ fontSize: 10 }} onClick={() => { setHideMobileTip(true); localStorage.setItem("stats_hide_mobile_tip", "1"); }} />
        </Alert>
        )}

        {/* Frescura del dato en vivo. `liveData.ts` es hora LOCAL de la PC del
            estacionamiento (StatsProcessor.py:197), sin timezone: se parsea tal
            cual. Si la PC esta apagada o sin internet, el dueno veia numeros
            viejos sin ninguna senal de que lo eran. */}
        {liveData?.ts && (
          <div className="text-muted mb-3" style={{ fontSize: 12 }}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
              schedule
            </span>
            Datos en vivo actualizados {moment(liveData.ts, "YYYY-MM-DDTHH:mm:ss").fromNow()}
            <span className="ms-1">
              ({moment(liveData.ts, "YYYY-MM-DDTHH:mm:ss").format("DD/MM HH:mm")})
            </span>
          </div>
        )}

        {/* Tabs navigation */}
        <Nav tabs className="mb-3">
          {TABS.map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                className={activeTab === tab.id ? "active" : ""}
                onClick={() => setActiveTab(tab.id)}
                style={{ cursor: "pointer" }}
              >
                <span
                  className="material-symbols-rounded me-1"
                  style={{ fontSize: 16, verticalAlign: "middle" }}
                >
                  {tab.icon}
                </span>
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {/* Tab content */}
        <TabContent activeTab={activeTab}>
          <TabPane tabId="resumen">
            {activeTab === "resumen" && (
              <TabResumen dailyDocs={dailyDocs} monthlyDocs={monthlyDocs} liveData={liveData} />
            )}
          </TabPane>
          <TabPane tabId="ingresos">
            {activeTab === "ingresos" && (
              <TabIngresos dailyDocs={dailyDocs} liveData={liveData} />
            )}
          </TabPane>
          <TabPane tabId="tickets">
            {activeTab === "tickets" && (
              <TabTickets dailyDocs={dailyDocs} />
            )}
          </TabPane>
          <TabPane tabId="mensual">
            {activeTab === "mensual" && (
              <TabMensual monthlyDocs={monthlyDocs} dailyDocs={dailyDocs} />
            )}
          </TabPane>
          <TabPane tabId="asistente">
            {activeTab === "asistente" && (
              <TabAsistente
                dailyDocs={dailyDocs}
                monthlyDocs={monthlyDocs}
                liveData={liveData}
                companyId={company_id}
              />
            )}
          </TabPane>
        </TabContent>

        {/* Ver la nota en Stats2: va DENTRO del Container porque
            `.page-content` reserva abajo el alto del footer fijo. */}
        <NotaDatoOriginal />
        {pie}
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  company_id: state.companies.currentCompany?.id,
  syncVersion: state.data.syncVersion,
  versionDB: state.data.appConfig?.version_app || "0.0.0",
});

export default connect(mapStateToProps)(Stats);
